import React, { useEffect, useState, useRef } from 'react';
import { Box, Dialog, Typography, DialogTitle, DialogContent, DialogActions, Button, Divider, Paper, Grid, Alert, IconButton } from '@mui/material';
import TimerIcon from '@mui/icons-material/Timer';
import { styled } from '@mui/system';
import { PauseCircleOutline, Coffee, PlayArrow, Login, Logout, Close } from "@mui/icons-material";
import axios from 'axios';
import { axiosInstance, localAxiosGet, localAxiosPut, localAxiosToken } from '../Axios/axios';
import { useSelector } from 'react-redux'; // Add this import
import { useNavigate } from 'react-router-dom';

const StyledTimerIcon = styled(TimerIcon)(({ theme }) => ({
    border: `1px solid #2b3d4f`,
    padding: '12px',
    borderRadius: '10px',
    backgroundColor: "#2b3d4f",
    color: '#FFFFFF',
    fontSize: '2.5rem',
    cursor: 'pointer',
}));

const AttendanceTimerWithoutTimers = ({ status, setStatus }) => {
    const [isTimerDialogBoxOpen, setIsTimerDialogBoxOpen] = useState(false);
    const [showCheckInText, setShowCheckInText] = useState(false);

    // Get accessToken from Redux instead of localStorage
    const accessToken = useSelector(state => state.auth.accessToken);
    
    const [breakStartTime, setBreakStartTime] = useState(null);
    const [isOnBreak, setIsOnBreak] = useState(false);
    const [breakHours, setBreakHours] = useState(0);
    const [breakMinutes, setBreakMinutes] = useState(0);
    const [breakSeconds, setBreakSeconds] = useState(0);
    const [showBreakTimer, setShowBreakTimer] = useState(false);

    const breakTimerRef = useRef(null);

    const [showCheckInTime, setShowCheckInTime] = useState('');
    const [showCheckoutTime, setShowCheckoutTime] = useState('');
    const [showBreakTime, setShowBreakTime] = useState('');
    const [showTotalTime, setShowTotalTime] = useState('');
    const [checkInTimeButton, setCheckInTimeButton] = useState('');
    const [checkOutTimeButton, setCheckOutTimeButton] = useState('');
    const [isCheckoutButtonDisabled, setIsCheckoutButtonDisabled] = useState(false);
    const [isAfterHours, setIsAfterHours] = useState(false);

    const userId = localStorage.getItem('userId');
    const userDetailsStorage = localStorage.getItem('userDetailsStorage');
    const [openTokenExpirePopup, setOpenTokenExpirePopup] = useState(false);
    const navigate = useNavigate();
    const [ipAddress, setIpAddress] = useState("");
    const [openCheckoutConfirm, setOpenCheckoutConfirm] = useState(false);

    let userDetails = null;

    if (userDetailsStorage) {
        try {
            userDetails = JSON.parse(userDetailsStorage);
        } catch (error) {
            console.error('Error parsing userDetails from localStorage:', error);
            localStorage.removeItem('userDetailsStorage');
        }
    }

    const formatTotalTime = (totalTimeString) => {
        if (!totalTimeString) {
            return '--:--';
        }

        const [hours, minutes] = totalTimeString.split(':');

        if (parseInt(hours) > 0) {
            return `${parseInt(hours)}hr ${parseInt(minutes)}min`;
        } else {
            return `${parseInt(minutes)}min`;
        }
    };

    const formatTimeToAMPM = (dateString) => {
        if (!dateString) {
            return '--:-- --';
        }

        let date;
        if (typeof dateString === 'string') {
            date = new Date(dateString);
        } else {
            date = dateString;
        }

        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const formattedHours = hours % 12 === 0 ? 12 : hours % 12;
        return `${String(formattedHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${ampm}`;
    }

    const calculateBreakDuration = () => {
        if (!breakStartTime) return 0;

        const now = new Date();
        const breakDurationMs = now.getTime() - breakStartTime.getTime();
        return Math.floor(breakDurationMs / (1000 * 60));
    };

    const handleMouseEnter = () => {
        setShowCheckInText(true);
    };

    const handleMouseLeave = () => {
        setShowCheckInText(false);
    };

    const handleTimerIconClick = () => {
        fetchTodayAttendanceRecord();
        setIsTimerDialogBoxOpen(true);

        if (isOnBreak && breakStartTime) {
            updateBreakTimerDisplay();
        }
    }

    const handleTimerDialogBoxClose = () => {
        setIsTimerDialogBoxOpen(false);
    }

    const isCheckoutTimeDisabled = () => {
        const now = new Date();
        const currentHour = now.getHours();
        return currentHour >= 19 || currentHour < 0;
    };

    const startBreakTimer = () => {
        localStorage.setItem('breakStartTime', new Date().toISOString());

        if (breakTimerRef.current) {
            clearInterval(breakTimerRef.current);
        }

        breakTimerRef.current = setInterval(updateBreakTimerDisplay, 1000);
    };

    const updateBreakTimerDisplay = () => {
        const storedBreakStartTime = localStorage.getItem('breakStartTime');
        if (!storedBreakStartTime) return;

        const startTime = new Date(storedBreakStartTime);
        const now = new Date();

        const elapsedMs = now - startTime;

        const seconds = Math.floor((elapsedMs / 1000) % 60);
        const minutes = Math.floor((elapsedMs / (1000 * 60)) % 60);
        const hours = Math.floor(elapsedMs / (1000 * 60 * 60));

        setBreakHours(hours);
        setBreakMinutes(minutes);
        setBreakSeconds(seconds);
    };

    const stopBreakTimer = () => {
        if (breakTimerRef.current) {
            clearInterval(breakTimerRef.current);
            breakTimerRef.current = null;
        }

        localStorage.removeItem('breakStartTime');
    };

    const resetBreakTimer = () => {
        setBreakHours(0);
        setBreakMinutes(0);
        setBreakSeconds(0);
        setShowBreakTimer(false);
    };

    const fetchTodayAttendanceRecord = async () => {
        try {
            if (!userId) {
                console.error("User ID not found");
                return;
            }

            // No need to check accessToken here - axiosInstance handles it
            const response = await axiosInstance.get(
                `/organization/getTodayAttendanceRecordForEmployee?user_id=${userId}`
            );

            console.log('Attendance response:', response.data);

            if (response && response.data && response.data.statusCode === 200) {
                const attendanceRecord = response.data.result;
                console.log('attendance record:', attendanceRecord);

                if (attendanceRecord.check_in) {
                    const checkInTimeFormatted = formatTimeToAMPM(attendanceRecord.check_in);
                    setShowCheckInTime(checkInTimeFormatted);
                    setCheckInTimeButton(checkInTimeFormatted);

                    if (attendanceRecord.break_duration > 0) {
                        const breakTimeFormattedInMin = formatBreakDuration(attendanceRecord.break_duration);
                        setShowBreakTime(breakTimeFormattedInMin);
                    }

                    if (attendanceRecord.check_out) {
                        const checkOutTimeFormatted = formatTimeToAMPM(attendanceRecord.check_out);
                        setShowCheckoutTime(checkOutTimeFormatted);
                        setCheckOutTimeButton(checkOutTimeFormatted);
                        setIsCheckoutButtonDisabled(true);
                        setIsOnBreak(false);
                        setStatus('OUT');
                    } else {
                        setStatus('IN');
                    }

                    if (attendanceRecord.total_hours && attendanceRecord.total_hours !== '00:00:00') {
                        const totalHoursFormatted = formatTotalTime(attendanceRecord.total_hours);
                        setShowTotalTime(totalHoursFormatted);
                    }
                }
            }
        } catch (error) {
            console.error("Failed to fetch today's attendance record:", error);
        }
    };

    useEffect(() => {
        const fetchIp = async () => {
            try {
                const res = await axios.get('https://api.ipify.org?format=json');
                setIpAddress(res.data.ip);
            } catch (err) {
                console.error("Failed to get IP", err);
            }
        };
        fetchIp();
    }, []);

    // Fetch attendance when component mounts AND when accessToken becomes available
    useEffect(() => {
        if (accessToken) {
            fetchTodayAttendanceRecord();
        }
    }, [accessToken]); // Add accessToken as dependency

    useEffect(() => {
        const isAfterHoursNow = isCheckoutTimeDisabled();
        setIsAfterHours(isAfterHoursNow);

        const storedBreakStartTime = localStorage.getItem('breakStartTime');
        if (storedBreakStartTime) {
            const startTime = new Date(storedBreakStartTime);
            setBreakStartTime(startTime);
            setIsOnBreak(true);
            setShowBreakTimer(true);

            breakTimerRef.current = setInterval(updateBreakTimerDisplay, 1000);

            setStatus('BREAK');
        }

        const intervalId = setInterval(() => {
            const isAfterHoursUpdated = isCheckoutTimeDisabled();
            setIsCheckoutButtonDisabled(isAfterHoursUpdated);
            setIsAfterHours(isAfterHoursUpdated);
        }, 60 * 1000);

        return () => {
            clearInterval(intervalId);
            if (breakTimerRef.current) {
                clearInterval(breakTimerRef.current);
            }
        };
    }, []);

    const handleCheckInButton = async () => {
        try {
            if (!userId) {
                console.error("User ID not found in localStorage");
                alert("User ID not found. Please log in again.");
                return;
            }

            const data = {
                user_id: userId,
                ip_address: ipAddress
            };

            const response = await axiosInstance.post('/organization/markAttendance', data);

            if (response && response.data && response.data.status === "SUCCESS") {
                console.log('mark attendance', response.data);

                const attendanceData = response.data.message.attendance;

                const checkInTimeFormatted = formatTimeToAMPM(attendanceData.check_in);
                setShowCheckInTime(checkInTimeFormatted);
                setCheckInTimeButton(checkInTimeFormatted);

                const breakTimeformattedInMin = formatBreakDuration(attendanceData.break_duration);
                setShowBreakTime(breakTimeformattedInMin);

                if (attendanceData.total_hours && attendanceData.total_hours !== '00:00:00') {
                    const totalHoursFormatted = formatTotalTime(attendanceData.total_hours);
                    setShowTotalTime(totalHoursFormatted);
                }

                setStatus('IN');

                // Remove the reload - just refetch the data
                setTimeout(() => {
                    fetchTodayAttendanceRecord();
                }, 1000);
            }
        } catch (error) {
            console.error("An error occurred during check-in:", error);
        }
    };

    const handleCheckOutButton = async () => {
        stopBreakTimer();
        resetBreakTimer();

        const now = new Date();
        const checkOutTimeFormatted = formatTimeToAMPM(now);
        setShowCheckoutTime(checkOutTimeFormatted);
        setCheckOutTimeButton(checkOutTimeFormatted);

        if (!userId) {
            console.error("User ID not found in localStorage");
            return;
        }

        const formattedDateTime = now.toISOString();

        const data = {
            user_id: userId,
            check_out: formattedDateTime,
            ip_address: ipAddress
        };

        try {
            const response = await axiosInstance.put('/organization/updateAttendanceByToday', data);

            if (response && response.data && response.data.status === "SUCCESS") {
                const resultData = response.data.result;

                const breakTimeformattedInMin = formatBreakDuration(resultData.break_duration);
                setShowBreakTime(breakTimeformattedInMin);

                const totalTimeFormatted = formatTotalTime(resultData.total_hours);
                setShowTotalTime(totalTimeFormatted);

                setIsOnBreak(false);
                setBreakStartTime(null);
                setStatus('OUT');
                setIsCheckoutButtonDisabled(true);

                // Remove the reload - just refetch the data
                setTimeout(() => {
                    fetchTodayAttendanceRecord();
                }, 1000);
            }
        } catch (error) {
            console.error("API call failed:", error);
        }
    };

    const handleBreakButton = () => {
        const now = new Date();
        setBreakStartTime(now);
        setIsOnBreak(true);
        setShowBreakTimer(true);
        fetchTodayAttendanceRecord();
        startBreakTimer();

        setStatus('BREAK');
    }

    const handleResumeButton = async () => {
        try {
            const storedBreakStartTime = localStorage.getItem('breakStartTime');
            if (!storedBreakStartTime) {
                console.error("Break start time not found in localStorage");
                return;
            }

            const startTime = new Date(storedBreakStartTime);

            stopBreakTimer();

            const now = new Date();
            const breakDurationMs = now.getTime() - startTime.getTime();
            const breakDurationMinutes = Math.floor(breakDurationMs / (1000 * 60));

            resetBreakTimer();

            const data = {
                user_id: userId,
                break_duration: breakDurationMinutes
            };

            console.log("Sending break duration to API:", breakDurationMinutes, "minutes");

            const response = await axiosInstance.put('/organization/updateAttendanceByToday', data);
            console.log("Break duration update response:", response.data);

            setIsOnBreak(false);
            setBreakStartTime(null);
            setStatus('IN');

            const breakTimeformattedInMin = formatBreakDuration(response.data.result.break_duration);
            setShowBreakTime(breakTimeformattedInMin);

        } catch (error) {
            console.error("An error occurred during resume:", error);
        }
    };

    const formatBreakDuration = (minutes) => {
        if (minutes < 1) {
            return '--:--';
        }

        if (minutes < 60) {
            return `${minutes}min`;
        }

        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        return `${hours}hr ${remainingMinutes}min`;
    };

    return (
        <div>
            <Box sx={{ position: 'absolute', right: 0, top: { xs: '95px', md: '85px' }, alignItems: 'center', zIndex: 100 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {showCheckInText && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                backgroundColor: "#ECF0F1",
                                color: "#2C3E50",
                                padding: "6px 8px",
                                borderRadius: "8px",
                            }}
                        >
                            <Typography sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                                Check-In
                            </Typography>
                        </Box>
                    )}

                    <StyledTimerIcon
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        onClick={handleTimerIconClick}
                    />
                </Box>

                <Dialog
                    open={isTimerDialogBoxOpen}
                    onClose={handleTimerDialogBoxClose}
                    aria-labelledby='timer-dialog'
                    PaperProps={{
                        style: {
                            borderRadius: '12px',
                            padding: '16px',
                            width: '90vw',
                            height: 'auto'
                        },
                    }}
                >
                    <DialogTitle sx={{
                        color: '#000000',
                        borderRadius: '8px 8px 0 0',
                        fontWeight: 'Bold'
                    }}>
                        Welcome Back!
                        <IconButton
                            onClick={handleTimerDialogBoxClose}
                            sx={{
                                position: 'absolute',
                                top: 20,
                                right: 30,
                                bgcolor: '#E0E0E0',
                                '&:hover': {
                                    bgcolor: '#CCCCCC',
                                    transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s',
                                zIndex: 1000
                            }}
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Divider sx={{ width: '100%', mb: 3 }} />
                         
                            <Box>
                                {isCheckoutButtonDisabled && (
                                    <Typography sx={{ color: '#333', my: 2 }}>
                                        Today's Attendance is marked, Please CheckIn tomorrow
                                    </Typography>
                                )}

                                <Box sx={{ display: 'flex', gap: '20px', justifyContent: 'center' }}>
                                    {!checkInTimeButton ? (
                                        !isAfterHours ? (
                                            <Button
                                                color="primary"
                                                variant="contained"
                                                onClick={handleCheckInButton}
                                                startIcon={<Login />}
                                                sx={{
                                                    backgroundColor: "#4CAF50",
                                                    color: "#fff",
                                                    fontWeight: "bold",
                                                    borderRadius: "8px",
                                                    padding: "8px 16px",
                                                    "&:hover": { backgroundColor: "#388E3C" },
                                                }}
                                            >
                                                Check-In
                                            </Button>
                                        ) : (
                                            <Button
                                                color="primary"
                                                variant="contained"
                                                disabled={true}
                                                startIcon={<Login />}
                                                sx={{
                                                    backgroundColor: "#BDBDBD",
                                                    color: "#fff",
                                                    fontWeight: "bold",
                                                    borderRadius: "8px",
                                                    padding: "8px 16px",
                                                    "&.Mui-disabled": {
                                                        backgroundColor: "#BDBDBD",
                                                        color: "#fff",
                                                        cursor: "not-allowed",
                                                    },
                                                }}
                                            >
                                                Check-In
                                            </Button>
                                        )
                                    ) : (
                                        <Button
                                            variant="contained"
                                            onClick={() => {
                                                if (isCheckoutButtonDisabled) return;
                                                setOpenCheckoutConfirm(true);
                                            }}
                                            disabled={isCheckoutButtonDisabled}
                                            startIcon={<Logout />}
                                            sx={{
                                                backgroundColor: isCheckoutButtonDisabled || checkOutTimeButton ? "#BDBDBD" : "#D32F2F",
                                                color: "#fff",
                                                fontWeight: "bold",
                                                borderRadius: "8px",
                                                padding: "8px 16px",
                                                "&:hover": { backgroundColor: isCheckoutButtonDisabled || checkOutTimeButton ? "#BDBDBD" : "#B71C1C" },
                                                "&.Mui-disabled": {
                                                    backgroundColor: "#BDBDBD",
                                                    color: "#fff",
                                                    cursor: "not-allowed",
                                                },
                                            }}
                                        >
                                            Check-Out
                                        </Button>
                                    )}
                                </Box>
                            </Box>

                            {isAfterHours && (
                                <Alert
                                    severity="info"
                                    sx={{
                                        mt: 2,
                                        width: '100%',
                                        borderRadius: '8px',
                                        backgroundColor: '#e3f2fd',
                                        '& .MuiAlert-icon': {
                                            color: '#1976d2'
                                        }
                                    }}
                                >
                                    Today's attendance is closed. Please check in tomorrow.
                                </Alert>
                            )}

                            <Divider sx={{ width: '100%', mt: 3 }} />
                        </Box>
                        <Grid container spacing={2}>
                            <Grid item xs={6} md={4}>
                                <Paper sx={{ px: 2, py: 3, backgroundColor: '#e8f5e9', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Login sx={{ fontSize: '2.5rem', color: '#388e3c', }} />
                                    <Typography sx={{ color: '#000000', fontWeight: 'Bold', mt: 2, mb: '2px' }}>
                                        {showCheckInTime || '--:--'}
                                    </Typography>
                                    <Typography sx={{ color: '#388e3c' }}>
                                        Check In
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6} md={4}>
                                <Paper sx={{ px: 2, py: 3, backgroundColor: '#ffebee', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Logout sx={{ fontSize: '2.5rem', color: '#e53935' }} />
                                    <Typography sx={{ color: '#000000', fontWeight: 'Bold', mt: 2, mb: '2px' }}>
                                        {showCheckoutTime || '--:--'}
                                    </Typography>
                                    <Typography sx={{ color: '#e53935' }}>
                                        Check Out
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6} md={4}>
                                <Paper sx={{ px: 2, py: 3, backgroundColor: '#f3e5f5', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <TimerIcon sx={{ fontSize: '2.5rem', color: '#9c27b0' }} />
                                    <Typography sx={{ color: '#000000', fontWeight: 'Bold', mt: 2, mb: '2px' }}>
                                        {showTotalTime || '--:--'}
                                    </Typography>
                                    <Typography sx={{ fontWeight: '500', color: '#9c27b0' }}>
                                        Total Hours
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    </DialogContent>
                </Dialog>

                <Dialog
                    open={openCheckoutConfirm}
                    onClose={() => setOpenCheckoutConfirm(false)}
                    PaperProps={{
                        style: {
                            borderRadius: '12px',
                            padding: '16px',
                            width: '90%',
                            maxWidth: '400px'
                        }
                    }}
                >
                    <DialogTitle sx={{ fontWeight: 'bold' }}>
                        Confirm Checkout
                    </DialogTitle>
                    <DialogContent>
                        <Typography>Are you sure you want to check out?</Typography>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                        <Button
                            onClick={() => setOpenCheckoutConfirm(false)}
                            variant="outlined"
                            sx={{
                                borderRadius: '8px',
                                px: 3,
                                mr: 2
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                setOpenCheckoutConfirm(false);
                                handleCheckOutButton();
                            }}
                            variant="contained"
                            color="error"
                            sx={{
                                borderRadius: '8px',
                                px: 3
                            }}
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </div>
    )
}

export default AttendanceTimerWithoutTimers